import { useState } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/hooks/useI18n';
import { NotificationActionService } from '@/services/notifications/NotificationActionService';
import { Notification } from '@/types/notification.types';
import { cn } from '@/lib/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const PAGE_SIZE = 3;
const PAGE_INCREMENT = 5;
const PAGE_MAX = 18;

export const NotificationBell = () => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();

  // Gestisce il click su una notifica
  const handleNotificationClick = async (notification: Notification) => {
    // Segna come letta se non lo è già
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Esegui l'azione se presente
    if (notification.action) {
      const actionService = NotificationActionService.create(navigate);
      actionService.executeAction(notification.action);
    }

    // Chiudi il dropdown
    setIsOpen(false);
  };

  // Gestisce il click su "segna tutte come lette"
  const handleMarkAllAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await markAllAsRead();
  };

  // Formatta la data in modo user-friendly
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.justNow');
    if (diffMins < 60) return t('notifications.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('notifications.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('notifications.daysAgo', { count: diffDays });
    
    return date.toLocaleDateString('it-IT', { month: 'short', day: 'numeric' });
  };

  const visibleNotifications = notifications.slice(0, visibleCount);
  const hasMore = visibleCount < notifications.length && visibleCount < PAGE_MAX;

  const handleOpenChange = (open: boolean) => {
    if (!open) setVisibleCount(PAGE_SIZE);
    setIsOpen(open);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-[hsl(var(--button-primary))] hover:bg-[hsl(var(--button-secondary))] active:bg-[hsl(var(--button-secondary-pressed))] disabled:text-[hsl(var(--button-outline-border))] relative p-0 h-10 w-10 md:h-8 md:w-8 rounded-full bg-gray-200"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4 text-gray-700" />

          {/* Counter Badge */}
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 flex items-center justify-center h-5 w-5 text-xs font-bold text-white bg-red-500 rounded-full">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-96 p-0 bg-white rounded-lg shadow-lg border border-gray-200 max-h-[500px] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between py-2 px-4 border-b border-gray-200">
          <h4 className="text-gray-900">
            {t('notifications.title')}
          </h4>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="flex items-center gap-1 text-sm text-gray-900 hover:text-black hover:underline"
            >
              <CheckCheck className="h-4 w-4" />
              {t('notifications.markAllAsRead')}
            </button>
          )}
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <Bell className="h-12 w-12 text-gray-300 mb-3" />
              <p className="text-gray-500">{t('notifications.noNotifications')}</p>
              <p className="text-sm text-gray-400 mt-1">
                {t('notifications.noNotificationsDesc')}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {visibleNotifications.map((notification) => {
                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={cn(
                      "p-3 cursor-pointer transition-colors",
                      notification.read
                        ? "bg-white hover:bg-gray-50 opacity-60"
                        : "bg-blue-50/30 hover:bg-blue-50/50",
                    )}
                  >
                    <div className="flex gap-3">
                      {/* Content */}
                      {/* Read indicator */}
                      {!notification.read && (
                        <div className="flex-shrink-0 flex items-start items-center pt-1">
                          <span className="h-2 w-2 rounded-full bg-red-500" />
                        </div>
                      )}
                      {notification.read && (
                        <div className="flex-shrink-0 flex items-start items-center pt-1">
                          <span className="h-2 w-2 rounded-full bg-gray-300" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p
                          className={cn(
                            "text-sm font-semibold mb-1",
                            notification.read
                              ? "text-gray-600"
                              : "text-gray-900",
                          )}
                        >
                          {notification.title}
                        </p>
                        <p
                          className={cn(
                            "text-sm",
                            notification.read
                              ? "text-gray-500"
                              : "text-gray-700",
                          )}
                        >
                          {notification.message}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-1">
                          {formatDate(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {hasMore && (
          <div className="p-2 border-t border-gray-100 text-center">
            <button
              onClick={() => setVisibleCount((c) => c + PAGE_INCREMENT)}
              className="text-sm text-gray-900 hover:text-black hover:underline"
            >
              {t('notifications.loadMore')}
            </button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
